import React, { useEffect, useState } from 'react';
import {
    Input,
    Select,
    Switch,
    Button,
    Space,
    Row,
    Col,
    Divider,
    Typography,
    Card,
    Alert
} from 'antd';
import {
    BankOutlined,
    UserOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    SaveOutlined,
    CloseOutlined,
    PlusCircleOutlined,
    EditOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import SDIModal from '../../../components/ui/SDIModal';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// Palette de couleurs
const colors = {
    primary: '#242166',
    secondary: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    danger: '#ff4d4f',
    light: '#f0f2f5',
    gradient1: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
};

const AddAgenceModal = ({
    visible,
    setVisible,
    membre,
    onCancel,
    mode = 'create',
    agenceData,
    villes = [],
    loading = false,
    onSaved,
}) => {
    const [codeAgence, setCodeAgence] = useState('');
    const [nomAgence, setNomAgence] = useState('');
    const [emailAgence, setEmailAgence] = useState('');
    const [villeAgence, setVilleAgence] = useState('LOME');
    const [typeAgence, setTypeAgence] = useState('AGENCE');
    const [responsableAgence, setResponsableAgence] = useState('');
    const [telephone1Agence, setTelephone1Agence] = useState('');
    const [telephone2Agence, setTelephone2Agence] = useState('');
    const [activeAgency, setActiveAgency] = useState(false);
    const [moreSwitchActive, setMoreSwitchActive] = useState(false);
    const [existingCodeAgence, setExistingCodeAgence] = useState(false);
    const [existingNomAgence, setExistingNomAgence] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const isEditMode = mode === 'edit';

    const navigate = useNavigate();

    const assignDefaultCommissionRates = async (newAgence) => {
        try {
            const organismeCode = newAgence?.organisme?.code || membre?.code;
            const agenceType = (newAgence?.typeAgence || typeAgence || "").toLowerCase();

            if (!newAgence?.codeId || !organismeCode || !agenceType) {
                return;
            }

            const { data } = await axiosInstance.get(`/api/taux-commission`);
            const defaultRates = (data || []).filter((tc) => {
                const tcType = (typeof tc?.typeIntermediaire === "string"
                    ? tc.typeIntermediaire
                    : tc?.typeIntermediaire?.libelle || tc?.typeIntermediaire?.nom || ""
                ).toLowerCase();
                const tcOrgCode = tc?.organisme?.code || tc?.organisme?.codeOrganisme;
                return tcType === agenceType && tcOrgCode === organismeCode;
            });

            if (!defaultRates.length) {
                return;
            }

            const payload = {
                agenceId: newAgence.codeId,
                commissions: defaultRates.map((tc) => ({
                    categorie: tc.categorie,
                    taux: tc.taux,
                })),
            };

            await axiosInstance.put(`/api/agence/taux-commission`, payload);
        } catch (error) {
            console.log(error);
        }
    };

    // Charger les données de l'agence en mode édition
    useEffect(() => {
        if (isEditMode && agenceData && visible) {
            console.log("Updates");
            setCodeAgence(agenceData.codeAgence || '');
            setNomAgence(agenceData.nom || '');
            setVilleAgence(agenceData.ville || 'LOME');
            setTypeAgence(agenceData.typeAgence || 'AGENCE');
            setResponsableAgence(agenceData.responsable || '');
            setEmailAgence(agenceData.email || '');

            // Gestion des contacts
            // if (agenceData.contact && Array.isArray(agenceData.contact)) {
            setTelephone1Agence(agenceData?.telephone1Agence || (agenceData.contact && agenceData.contact[0]) || '');
            setTelephone2Agence(agenceData?.telephone2Agence || (agenceData.contact && agenceData.contact[1]) || '');
            // }

            setActiveAgency(agenceData.restrictionProduction || false);
        }
    }, [isEditMode, agenceData, visible]);

    useEffect(() => {
        if (!visible && !isEditMode) {
            resetForm();
            setMoreSwitchActive(false);
            setExistingCodeAgence(false);
            setExistingNomAgence(false);
        }
    }, [visible, isEditMode]);

    const validateForm = () => {
        const newErrors = {};

        if (!codeAgence || codeAgence.length < 3) {
            newErrors.codeAgence = 'Le code doit contenir au moins 3 caractères';
        }

        if (!nomAgence || nomAgence.length < 3) {
            newErrors.nomAgence = 'Le nom doit contenir au moins 3 caractères';
        }

        if (!villeAgence) {
            newErrors.villeAgence = 'La ville est requise';
        }

        if (!telephone1Agence) {
            newErrors.telephone1Agence = 'Le téléphone est requis';
        } else if (!/^[0-9]{8}$/.test(telephone1Agence.replace(/\s/g, ''))) {
            newErrors.telephone1Agence = 'Format invalide (8 chiffres requis)';
        }

        if (telephone2Agence && !/^[0-9]{8}$/.test(telephone2Agence.replace(/\s/g, ''))) {
            newErrors.telephone2Agence = 'Format invalide (8 chiffres requis)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm() || existingCodeAgence || existingNomAgence) {
            return;
        }
        var contactsAgence = [];
        telephone2Agence
            ? contactsAgence.push(telephone1Agence, telephone2Agence)
            : contactsAgence.push(telephone1Agence);
        const agenceFormData = {
            codeAgence,
            nom: nomAgence,
            ville: villeAgence,
            email: emailAgence,
            typeAgence,
            responsable: responsableAgence,
            telephone1Agence: telephone1Agence.replace(/\s/g, ''),
            telephone2Agence: telephone2Agence.replace(/\s/g, ''),
            contact: contactsAgence,
            restrictionProduction: activeAgency,
            organisme: membre,
        };

        // En mode édition, ajouter l'ID de l'agence
        if (isEditMode && agenceData) {
            agenceFormData.codeId = agenceData.codeId;
            agenceFormData.agenceId = agenceData.agenceId;
        }

        console.log('Données de l\'agence à soumettre:', agenceFormData);
        const handleSuccess = (payload, message) => {
            toast(message);
            onSaved && onSaved(payload);
        };

        setSubmitting(true);
        if (isEditMode) {
            axiosInstance
                .put(`/api/agence`, JSON.stringify(agenceFormData))
                .then((res) => {
                    handleSuccess(res.data, "Agence mis à jour avec succès!");
                    setVisible(false);
                })
                .catch((err) => {
                    console.log(err);
                    toast("Erreur lors de l'ajout de l'agence");
                })
                .finally(() => setSubmitting(false));
        } else {
            axiosInstance
                .post(`/api/agence`, JSON.stringify(agenceFormData))
                .then(async (res) => {
                    handleSuccess(res.data, "Agence ajoutée avec succès!");
                    try {
                        await assignDefaultCommissionRates(res.data);
                    } catch (error) {
                        console.log(error);
                    }
                    resetForm();
                    if (!moreSwitchActive) {
                        setVisible(false);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    toast("Erreur lors de l'ajout de l'agence");
                })
                .finally(() => setSubmitting(false));
        }
    };

    const resetForm = () => {
        setCodeAgence('');
        setNomAgence('');
        setEmailAgence('');
        setVilleAgence('LOME');
        setTypeAgence('AGENCE');
        setResponsableAgence('');
        setTelephone1Agence('');
        setTelephone2Agence('');
        setActiveAgency(false);
        setErrors({});
        setExistingCodeAgence(false);
        setExistingNomAgence(false);
    };

    const handleCancel = () => {
        resetForm();
        setMoreSwitchActive(false);
        setExistingCodeAgence(false);
        setExistingNomAgence(false);
        onCancel();
    };

    const checkCodeAgence = (value) => {
        if (value && value.length > 3) {
            axiosInstance.get(`/api/agence/organisme/code/${value}/${membre?.code}`).then((res) => {
                if (res.data) {
                    setExistingCodeAgence(true);
                    toast("Une agence porte déjà ce code");
                } else {
                    setExistingCodeAgence(false);
                }
            }).catch((err) => {
                console.log(err);
            });
        }
    };

    const checkNomAgence = (value) => {
        if (value && value.length > 3) {
            axiosInstance.get(`/api/agence/organisme/nom/${value}/${membre?.code}`).then((res) => {
                // console.log(res.data);
                if (res.data) {
                    setExistingNomAgence(true);
                    toast("Une agence porte déjà ce nom");
                } else {
                    setExistingNomAgence(false);
                }
            }).catch((err) => {
                console.log(err);
            });
        }
    };

    return (
        <SDIModal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={900}
            centered
            style={{ top: 20 }}
            closeIcon={
                <CloseOutlined style={{ color: 'red', fontSize: '18px' }} />
            }
        >
            {/* Header avec dégradé */}
            <div
                style={{
                    background: colors.gradient1,
                    padding: '32px 40px',
                    borderRadius: '8px 8px 0 0'
                }}
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {isEditMode ? (
                        <EditOutlined style={{ fontSize: '48px', color: 'white' }} />
                    ) : (
                        <BankOutlined style={{ fontSize: '48px', color: 'white' }} />
                    )}
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                        {isEditMode ? 'Modifier l\'agence' : 'Ajouter une nouvelle agence'}
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                        {isEditMode ? (
                            <>Agence <span style={{ fontWeight: 'bold', color: 'white' }}>{agenceData?.nom}</span></>
                        ) : (
                            <>Pour <span style={{ fontWeight: 'bold', color: 'white' }}>{membre?.raisonSociale}</span></>
                        )}
                    </Text>
                </Space>
            </div>

            {/* Contenu du formulaire */}
            <div style={{ padding: '32px 40px', maxHeight: '70vh', overflowY: 'auto' }}>
                {existingCodeAgence && (
                    <Alert
                        message="Une agence porte déjà ce code"
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: '24px', borderRadius: '8px' }}
                        onClose={() => setExistingCodeAgence(false)}
                    />
                )}

                {existingNomAgence && (
                    <Alert
                        message="Une agence porte déjà ce nom"
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: '24px', borderRadius: '8px' }}
                        onClose={() => setExistingNomAgence(false)}
                    />
                )}

                {/* Section Informations principales */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <BankOutlined style={{ marginRight: '8px' }} />
                            Informations de l'agence
                        </Text>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Renseignez les informations principales de l'agence
                        </Text>
                    </Space>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Code de l'agence <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Ex: AG-001"
                                    value={codeAgence}
                                    initialValue={codeAgence}
                                    onChange={(e) => {
                                        setCodeAgence(e.target.value);
                                        checkCodeAgence(e.target.value);
                                    }}
                                    style={{ borderRadius: '8px' }}
                                    status={errors.codeAgence || existingCodeAgence ? 'error' : ''}
                                />
                                {errors.codeAgence && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.codeAgence}</Text>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Nom de l'agence <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Ex: Agence Lomé Centre"
                                    value={nomAgence}
                                    onChange={(e) => {
                                        setNomAgence(e.target.value);
                                        checkNomAgence(e.target.value);
                                    }}
                                    style={{ borderRadius: '8px' }}
                                    status={errors.nomAgence || existingNomAgence ? 'error' : ''}
                                />
                                {errors.nomAgence && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.nomAgence}</Text>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Ville <span style={{ color: colors.danger }}>*</span></span>
                                </div>
                                <Select
                                    size="large"
                                    placeholder="Sélectionnez une ville"
                                    value={villeAgence}
                                    onChange={setVilleAgence}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                    suffixIcon={<EnvironmentOutlined style={{ color: colors.primary }} />}
                                    status={errors.villeAgence ? 'error' : ''}
                                    options={villes.map((ville, id) => ({
                                        label: ville.libelleParametre,
                                        value: ville.libelleParametre,
                                        key: id,
                                    }))}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Type d'agence <span style={{ color: colors.danger }}>*</span></span>
                                </div>
                                <Select
                                    size="large"
                                    placeholder="Sélectionnez un type"
                                    value={typeAgence}
                                    onChange={setTypeAgence}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                >
                                    <Option value="AGENCE">Agence</Option>
                                    <Option value="BUREAU_DIRECT">Bureau Direct</Option>
                                    <Option value="AGENCE_GENERALE">Agence Générale</Option>
                                    {/* <Option value="COURTIER">Courtier</Option> */}
                                    <Option value="ETABLISSEMENT_FINANCIER">Établissement Financier</Option>
                                    <Option value="AUTRE">Autre</Option>
                                    <Option value="SIEGE">Siège</Option>
                                </Select>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Email de l'agence <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Ex: agence@compagnie.tg"
                                    value={emailAgence}
                                    onChange={(e) => {
                                        setEmailAgence(e.target.value);
                                    }}
                                    style={{ borderRadius: '8px' }}
                                    status={errors.emailAgence ? 'error' : ''}
                                />
                                {errors.emailAgence && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.emailAgence}</Text>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Section Responsable */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <UserOutlined style={{ marginRight: '8px' }} />
                            Responsable de l'agence
                        </Text>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Informations du responsable et contacts
                        </Text>
                    </Space>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Nom du responsable</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Ex: Jean DUPONT"
                                    prefix={<UserOutlined style={{ color: colors.primary }} />}
                                    value={responsableAgence}
                                    onChange={(e) => setResponsableAgence(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Numéro de téléphone 1 <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="90 12 34 56"
                                    prefix={<PhoneOutlined style={{ color: colors.primary }} />}
                                    addonBefore="TG (+228)"
                                    value={telephone1Agence}
                                    onChange={(e) => setTelephone1Agence(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    maxLength={8}
                                    status={errors.telephone1Agence ? 'error' : ''}
                                />
                                {errors.telephone1Agence && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.telephone1Agence}</Text>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Numéro de téléphone 2</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="91 23 45 67"
                                    prefix={<PhoneOutlined style={{ color: colors.primary }} />}
                                    addonBefore="TG (+228)"
                                    value={telephone2Agence}
                                    onChange={(e) => setTelephone2Agence(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    maxLength={8}
                                    status={errors.telephone2Agence ? 'error' : ''}
                                />
                                {errors.telephone2Agence && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.telephone2Agence}</Text>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Section Statut */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        background: activeAgency ? 'rgba(255, 77, 79, 0.05)' : 'rgba(82, 196, 26, 0.05)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Row align="middle" justify="space-between">
                        <Col>
                            <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                                    Statut de l'agence
                                </Text>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    {activeAgency ? "L'agence est actuellement inactive" : "L'agence est actuellement active"}
                                </Text>
                            </Space>
                        </Col>
                        <Col>
                            <Space align="center">
                                <Text style={{ fontWeight: 600, color: activeAgency ? colors.danger : colors.success }}>
                                    {activeAgency ? 'Inactive' : 'Active'}
                                </Text>
                                <Switch
                                    checked={activeAgency}
                                    onChange={setActiveAgency}
                                    style={{
                                        backgroundColor: activeAgency ? colors.danger : colors.success
                                    }}
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Divider style={{ margin: '24px 0' }} />

                {/* Footer avec actions */}
                <Row gutter={16} align="middle" justify="space-between">
                    <Col>
                        {!isEditMode && (<Space align="center">
                            <PlusCircleOutlined style={{ color: colors.primary, fontSize: '18px' }} />
                            <Text style={{ fontWeight: 600 }}>Créer plusieurs agences</Text>
                            <Switch
                                checked={moreSwitchActive}
                                onChange={setMoreSwitchActive}
                                style={{
                                    backgroundColor: moreSwitchActive ? colors.primary : undefined
                                }}
                            />
                        </Space>)}
                    </Col>

                    <Col>
                        <Space size={12}>
                            <Button
                                size="large"
                                onClick={handleCancel}
                                style={{
                                    borderRadius: '8px',
                                    height: '44px',
                                    minWidth: '120px'
                                }}
                                icon={<CloseOutlined />}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleSubmit}
                                loading={loading || submitting}
                                style={{
                                    backgroundColor: colors.primary,
                                    borderColor: colors.primary,
                                    borderRadius: '8px',
                                    height: '44px',
                                    minWidth: '140px'
                                }}
                                icon={<SaveOutlined />}
                            >
                                Enregistrer
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>
        </SDIModal>
    );
};

export default AddAgenceModal;
